import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectTrainingRunLanguages, selectTrainingRunSelectedLang, trainingRunActions } from '../../store/entities/trainingRun';
import { selectIdentity } from '../../store/entities/identity';
import { getAppTrainingLangList, selectAppTrainingLangList } from '../../store/entities/appTrainingLangList';
import { selectCompanyCustomizationTrainingLangCodes } from '../../store/entities/companyCustomization';
import { FetchState } from '../../store/fetchState';
import { isTourniquet } from '../../utils/roles';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { loadTourniquetLocale } from '../tourniquet/loadTourniquetLocale';
import { QcsBox } from '../common/basic/QcsBox';
import { useNavigation } from '../../utils/urlPaths';

// Maps an API language to its SUPPORTED_LANGUAGES equivalent by flag (reliable)
// while preserving the original API code for backend calls.
// Returns { ...SUPPORTED_LANGUAGES entry, apiCode: original API code }
const normalizeLanguage = (lang: { code: string; flag: string; name: string }) => {
  const supported = SUPPORTED_LANGUAGES.find((s) => s.flag.toLowerCase() === lang.flag.toLowerCase());
  return supported
    ? { code: supported.code, flag: supported.flag, name: supported.name, apiCode: lang.code }
    : { code: lang.code.toLowerCase(), flag: lang.flag, name: lang.name, apiCode: lang.code };
};

export const TrainingRunHeaderLanguageSwitcher: FC = () => {
  const { currentPath } = useNavigation();
  const { i18n } = useTranslation();
  const trainingRunLanguages = useAppSelector(selectTrainingRunLanguages);
  const selectedLang = useAppSelector(selectTrainingRunSelectedLang);
  const identity = useAppSelector(selectIdentity);
  const appTrainingLangList = useAppSelector(selectAppTrainingLangList);
  const companyCustomizationTrainingLangCodes = useAppSelector(selectCompanyCustomizationTrainingLangCodes);
  const dispatch = useAppDispatch();
  const isTourniquetUser = isTourniquet(identity);
  const isTrainingRun = currentPath.startsWith('/training-run');

  useEffect(() => {
    if (isTourniquetUser && appTrainingLangList.state === FetchState.None) {
      dispatch(getAppTrainingLangList());
    }
  }, [isTourniquetUser, appTrainingLangList.state, dispatch]);

  const tourniquetLanguages = useMemo(
    () =>
      appTrainingLangList.data?.content
        .filter((x) => companyCustomizationTrainingLangCodes?.includes(x.code))
        .map(normalizeLanguage),
    [appTrainingLangList.data?.content, companyCustomizationTrainingLangCodes],
  );

  const normalizedTrainingRunLanguages = useMemo(
    () => trainingRunLanguages.map(normalizeLanguage),
    [trainingRunLanguages],
  );

  const languages = isTrainingRun ? normalizedTrainingRunLanguages : (tourniquetLanguages ?? []);

  // On training-run pages, map the Redux selectedLang (API code) to the normalized frontend code
  const effectiveSelectedLang = useMemo(() => {
    if (!isTrainingRun) return i18n.language;
    const match = normalizedTrainingRunLanguages.find((l) => l.apiCode === selectedLang);
    return match?.code ?? selectedLang;
  }, [isTrainingRun, i18n.language, normalizedTrainingRunLanguages, selectedLang]);

  // Switches the whole UI to a language, lazy-loading its bundle when it isn't shipped with the app.
  // cs/en are always bundled; every other code (incl. de/pl/ru) uses the tourniquet locale files.
  const loadAndApplyLocale = useCallback(
    async (normalizedCode: string) => {
      if (normalizedCode !== 'cs' && normalizedCode !== 'en') {
        try {
          const translation = await loadTourniquetLocale(normalizedCode);
          i18n.addResourceBundle(normalizedCode, 'translation', translation);
        } catch (err) {
          console.warn(`Failed to load locale for "${normalizedCode}":`, err);
        }
      }
      i18n.changeLanguage(normalizedCode);
    },
    [i18n],
  );

  // The app language to restore once a run closes. Only used for non-tourniquet users, who have
  // normal pages to return to; tourniquet users' whole app stays in the (persisted) run language.
  const previousAppLangRef = useRef<string | null>(null);

  // Keeps the UI locale in sync with the training/questionnaire run for every user.
  useEffect(() => {
    // Not in a run (back on normal pages): restore the language we saved on entry, if any.
    if (!isTrainingRun) {
      if (previousAppLangRef.current) {
        i18n.changeLanguage(previousAppLangRef.current);
        previousAppLangRef.current = null;
      }
      return;
    }

    // Entering a run: remember the normal app language once, so we can restore it on exit.
    // Only non-tourniquet users need this — tourniquet users have no normal pages to return to.
    if (!isTourniquetUser && previousAppLangRef.current === null) {
      previousAppLangRef.current = i18n.language;
    }
    // Switch the whole UI to the selected run language (its default on entry, or a manual switch).
    if (effectiveSelectedLang && effectiveSelectedLang !== i18n.language) {
      loadAndApplyLocale(effectiveSelectedLang);
    }
  }, [isTrainingRun, isTourniquetUser, effectiveSelectedLang, i18n, loadAndApplyLocale]);

  const handleChange = useCallback(
    async (normalizedCode: string) => {
      if (normalizedCode === effectiveSelectedLang) return;

      if (isTrainingRun) {
        // Dispatch the original API code for backend calls; the effect above applies the UI locale.
        const apiLang = normalizedTrainingRunLanguages.find((l) => l.code === normalizedCode);
        dispatch(trainingRunActions.setSelectedLang(apiLang?.apiCode ?? normalizedCode));
      } else {
        // On non-run pages the switcher only renders for tourniquet users, so switch the app locale.
        await loadAndApplyLocale(normalizedCode);
      }
    },
    [effectiveSelectedLang, isTrainingRun, normalizedTrainingRunLanguages, dispatch, loadAndApplyLocale],
  );

  if (isTrainingRun && languages.length < 1) return null;
  if (!isTrainingRun && (!isTourniquetUser || languages.length < 1)) return null;

  return (
    <Select
      value={effectiveSelectedLang}
      onChange={(e) => handleChange(e.target.value)}
      variant="outlined"
      disabled={languages.length === 1}
      size="small"
      sx={{ marginLeft: 'auto' }}
      renderValue={(value) => {
        const lang = languages.find((l) => l.code === value);
        if (!lang) return null;
        return (
          <QcsBox sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <img src={'/flags/' + lang.flag + '.svg'} height="20" loading="lazy" alt={lang.name} />
            {lang.name}
          </QcsBox>
        );
      }}
    >
      {languages.map((language) => (
        <MenuItem key={language.code} value={language.code}>
          <QcsBox sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <img src={'/flags/' + language.flag + '.svg'} height="20" loading="lazy" alt={language.name} />
            {language.name}
          </QcsBox>
        </MenuItem>
      ))}
    </Select>
  );
};
